import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Typography
} from '@material-ui/core';
import { AlertOctagon } from 'lucide-react';
import React, { useState } from 'react';
import { useDialogState } from '../../hooks/useDialogState';
import { useTranslation } from '../../i18next';
import { AlertBox } from '../AlertBox';
import { ButtonWithPromise } from '../ButtonWithPromise';
import { DialogTitleWithTypography } from '../DialogTitleWithTypography';

type DeletionConfirmationDialogCustomBody = (args: {
  keyword: string | undefined;
  keywordValue: string;
  onChangeKeywordValue: (nextValue: string) => void;
}) => React.ReactNode;

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<any>;
  keyword?: string;
  dialogTitle?: string;
  buttonAction?: string;
  dialogBody?: DeletionConfirmationDialogCustomBody;
};

const TextFieldRow = styled('div')((p) => ({
  marginTop: p.theme.spacing(1)
}));

export const DeletionConfirmationDialogBody = ({
  onClose,
  onConfirm,
  keyword,
  dialogTitle,
  buttonAction,
  dialogBody
}: Omit<Props, 'open'>) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [keywordInput, setKeywordInput] = useState('');
  const confirm = async () => {
    const result = await onConfirm();
    onClose();
    return result;
  };
  return (
    <>
      <DialogTitleWithTypography>{dialogTitle}</DialogTitleWithTypography>
      {dialogBody ? (
        <DialogContent>
          {dialogBody({
            keyword,
            keywordValue: keywordInput,
            onChangeKeywordValue: setKeywordInput
          })}
        </DialogContent>
      ) : keyword ? (
        <DialogContent>
          <AlertBox
            color="red"
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: theme.spacing(3)
            }}
          >
            <AlertOctagon
              size={24}
              strokeWidth={1.8}
              style={{ marginRight: theme.spacing(1) }}
            />{' '}
            <Typography component="p">
              {t('deletionConfirmationDialog.alert')}
            </Typography>
          </AlertBox>
          <Typography component="p">
            {t('deletionConfirmationDialog.label')} <strong>{keyword}</strong>
          </Typography>
          <TextFieldRow>
            <TextField
              variant="outlined"
              fullWidth={true}
              autoFocus
              value={keywordInput}
              onChange={(ev) => setKeywordInput(ev.target.value)}
            />
          </TextFieldRow>
        </DialogContent>
      ) : (
        <DialogContent>
          <Typography component="p">
            {t('deletionConfirmationDialog.alert')}
          </Typography>
        </DialogContent>
      )}
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {t('Cancel')}
        </Button>
        <ButtonWithPromise
          variant="contained"
          color="secondary"
          disabled={keyword ? keywordInput !== keyword : false}
          pending={`${t('Deleting')}...`}
          onClick={confirm}
        >
          {buttonAction}
        </ButtonWithPromise>
      </DialogActions>
    </>
  );
};

export const DeletionConfirmationDialog: React.FC<Props> = ({
  open,
  onClose,
  onConfirm,
  keyword,
  dialogTitle,
  buttonAction,
  dialogBody
}) => {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DeletionConfirmationDialogBody
        onClose={onClose}
        onConfirm={onConfirm}
        keyword={keyword}
        dialogTitle={dialogTitle}
        buttonAction={buttonAction}
        dialogBody={dialogBody}
      />
    </Dialog>
  );
};

export const DeleteButton = ({
  keyword,
  dialogTitle = 'Delete?',
  buttonAction = 'Delete',
  onDelete,
  dialogBody,
  ...props
}: Omit<ButtonProps, 'onClick'> & {
  keyword?: string;
  dialogTitle?: string;
  buttonAction?: string;
  onDelete: () => Promise<any>;
  dialogBody?: DeletionConfirmationDialogCustomBody;
}) => {
  const { dialogOpen, openDialog, closeDialog } = useDialogState(false);
  return (
    <>
      <Button {...props} onClick={openDialog} />
      <DeletionConfirmationDialog
        open={dialogOpen}
        onClose={closeDialog}
        onConfirm={onDelete}
        keyword={keyword}
        dialogTitle={dialogTitle}
        buttonAction={buttonAction}
        dialogBody={dialogBody}
      />
    </>
  );
};
